import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconAlertCircle, IconPlus, IconTrash, IconUsersGroup } from "@/lib/icons";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { formatDate, formatDateTime } from "@/lib/format";
import { ApiError } from "@/lib/api/client";
import {
  type DeputyRead,
  createDeputy,
  listDeputies,
  revokeDeputy,
} from "@/lib/api/deputies";
import { listUsers } from "@/lib/api/users";

export function DeputiesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialogOpen, dialogHandlers] = useDisclosure(false);

  const { data, isLoading } = useQuery({
    queryKey: ["deputies"],
    queryFn: () => listDeputies(false),
  });

  const usersQuery = useQuery({
    queryKey: ["users-all"],
    queryFn: () => listUsers({ limit: 200 }),
  });

  const userById = new Map((usersQuery.data?.items ?? []).map((u) => [u.id, u]));

  const revokeMutation = useMutation({
    mutationFn: revokeDeputy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deputies"] });
      notifications.show({ color: "green", message: t("deputies.revoked") });
    },
  });

  const form = useForm({
    initialValues: {
      principal_user_id: "",
      deputy_user_id: "",
      valid_from: new Date(),
      valid_until: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      reason: "",
    },
    validate: {
      principal_user_id: (v) => (v ? null : t("validation.required")),
      deputy_user_id: (v, values) => {
        if (!v) return t("validation.required");
        if (v === values.principal_user_id) return t("deputies.same_user");
        return null;
      },
      valid_until: (v, values) =>
        v.getTime() > values.valid_from.getTime() ? null : t("deputies.invalid_range"),
    },
  });

  const createMutation = useMutation({
    mutationFn: createDeputy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deputies"] });
      notifications.show({ color: "green", message: t("deputies.created") });
      form.reset();
      dialogHandlers.close();
    },
  });

  const submit = form.onSubmit((values) =>
    createMutation.mutate({
      principal_user_id: values.principal_user_id,
      deputy_user_id: values.deputy_user_id,
      valid_from: values.valid_from.toISOString(),
      valid_until: values.valid_until.toISOString(),
      reason: values.reason || null,
    }),
  );

  const errorMessage = (() => {
    if (!createMutation.error) return null;
    if (createMutation.error instanceof ApiError) return createMutation.error.message;
    return t("auth.network_error");
  })();

  const isActive = (d: DeputyRead) => {
    if (d.revoked_at) return false;
    const now = Date.now();
    return (
      new Date(d.valid_from).getTime() <= now &&
      new Date(d.valid_until).getTime() >= now
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("deputies.title")}
        subtitle={t("deputies.subtitle")}
        back
        actions={
          <Button
            leftSection={<IconPlus size={16} strokeWidth={2.4} />}
            onClick={dialogHandlers.open}
            radius="xl"
          >
            {t("deputies.create")}
          </Button>
        }
      />

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={780}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("deputies.fields.principal")}</Table.Th>
                <Table.Th>{t("deputies.fields.deputy")}</Table.Th>
                <Table.Th>{t("deputies.fields.valid_from")}</Table.Th>
                <Table.Th>{t("deputies.fields.valid_until")}</Table.Th>
                <Table.Th>{t("deputies.fields.status")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {!isLoading && (data?.items.length ?? 0) === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <EmptyState
                      icon={<IconUsersGroup size={32} strokeWidth={1.5} />}
                      title={t("deputies.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((d) => (
                <Table.Tr key={d.id}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {userById.get(d.principal_user_id)?.full_name ?? d.principal_user_id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {userById.get(d.deputy_user_id)?.full_name ?? d.deputy_user_id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" c="dimmed">
                      {formatDateTime(d.valid_from)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" c="dimmed">
                      {formatDateTime(d.valid_until)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {d.revoked_at ? (
                      <Badge color="gray" variant="light" radius="sm">
                        {t("deputies.statuses.revoked")}
                      </Badge>
                    ) : isActive(d) ? (
                      <Badge color="brand" variant="light" radius="sm">
                        {t("deputies.statuses.active")}
                      </Badge>
                    ) : (
                      <Badge color="orange" variant="light" radius="sm">
                        {t("deputies.statuses.expired")}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {!d.revoked_at && (
                      <Tooltip label={t("deputies.revoke")}>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => revokeMutation.mutate(d.id)}
                          loading={
                            revokeMutation.isPending &&
                            revokeMutation.variables === d.id
                          }
                        >
                          <IconTrash size={16} strokeWidth={1.8} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal
        opened={dialogOpen}
        onClose={() => {
          form.reset();
          dialogHandlers.close();
        }}
        title={t("deputies.create_title")}
        size="md"
        radius="lg"
      >
        {errorMessage && (
          <TextInput value={errorMessage} disabled error mb="md" />
        )}
        <form onSubmit={submit}>
          <Stack>
            <Select
              label={t("deputies.fields.principal")}
              required
              searchable
              data={(usersQuery.data?.items ?? []).map((u) => ({
                value: u.id,
                label: `${u.full_name} (${t(`users.roles.${u.role}`)})`,
              }))}
              {...form.getInputProps("principal_user_id")}
            />
            <Select
              label={t("deputies.fields.deputy")}
              required
              searchable
              data={(usersQuery.data?.items ?? []).map((u) => ({
                value: u.id,
                label: `${u.full_name} (${t(`users.roles.${u.role}`)})`,
              }))}
              {...form.getInputProps("deputy_user_id")}
            />
            <Group grow>
              <DateTimePicker
                label={t("deputies.fields.valid_from")}
                required
                {...form.getInputProps("valid_from")}
              />
              <DateTimePicker
                label={t("deputies.fields.valid_until")}
                required
                {...form.getInputProps("valid_until")}
              />
            </Group>
            <Textarea
              label={t("deputies.fields.reason")}
              placeholder={t("deputies.reason_placeholder")}
              minRows={2}
              autosize
              {...form.getInputProps("reason")}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" color="gray" onClick={dialogHandlers.close}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={createMutation.isPending} radius="xl">
                {t("common.create")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
